import React, { useState } from 'react';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useAuth } from '../../contexts/AuthContext';
import { EmailTemplate, defaultEmailVariables, defaultEmailTemplates } from '../../types/emailTemplate';
import { emailService } from '../../services/emailService';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  StarIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const EmailTemplateManager: React.FC = () => {
  const { user } = useAuth();
  const { organizations } = useOrganizations();
  const { 
    templates, 
    loading, 
    createTemplate, 
    updateTemplate, 
    deleteTemplate,
    toggleTemplateActive,
    setDefaultTemplate 
  } = useEmailTemplates();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');

  const [formData, setFormData] = useState({
    name: '',
    type: 'notification' as EmailTemplate['type'],
    subject: '',
    htmlContent: '',
    textContent: '',
    description: '',
    organizationId: '',
    isActive: true,
    isDefault: false
  });

  const templateTypes = [
    { value: 'userInvitation', label: '👤 Felhasználó meghívó', color: 'blue' },
    { value: 'passwordReset', label: '🔐 Jelszó visszaállítás', color: 'red' },
    { value: 'notification', label: '📢 Értesítés', color: 'green' },
    { value: 'welcome', label: '👋 Üdvözlő', color: 'purple' },
    { value: 'roleChanged', label: '⚡ Szerepkör módosítás', color: 'orange' },
    { value: 'accountDeactivation', label: '🚫 Fiók deaktiválás', color: 'gray' },
    { value: 'taskAssigned', label: '✅ Feladat hozzárendelve', color: 'blue' },
    { value: 'taskCompleted', label: '✔️ Feladat befejezve', color: 'green' },
    { value: 'taskOverdue', label: '⚠️ Feladat lejárt', color: 'red' },
    { value: 'projectCreated', label: '📁 Projekt létrehozva', color: 'indigo' },
    { value: 'projectUpdated', label: '📝 Projekt frissítve', color: 'indigo' },
    { value: 'formSubmitted', label: '📋 Űrlap beküldve', color: 'teal' },
    { value: 'appointmentCreated', label: '📅 Időpont foglalva', color: 'cyan' },
    { value: 'appointmentUpdated', label: '🔄 Időpont módosítva', color: 'cyan' },
    { value: 'custom', label: '⚙️ Egyedi', color: 'gray' }
  ];

  const getTypeConfig = (type: string) => {
    return templateTypes.find(t => t.value === type) || templateTypes[0];
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'notification',
      subject: '',
      htmlContent: '',
      textContent: '',
      description: '',
      organizationId: '',
      isActive: true,
      isDefault: false
    });
  };

  const handleCreateTemplate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      htmlContent: template.htmlContent,
      textContent: template.textContent || '',
      description: template.description || '',
      organizationId: template.organizationId || '',
      isActive: template.isActive,
      isDefault: template.isDefault
    });
    setShowEditModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (!formData.name || !formData.subject || !formData.htmlContent) {
        toast.error('Kérjük töltse ki az összes kötelező mezőt!');
        return;
      }

      const templateData = {
        name: formData.name,
        type: formData.type,
        subject: formData.subject,
        htmlContent: formData.htmlContent,
        textContent: formData.textContent,
        description: formData.description,
        variables: defaultEmailVariables[formData.type]?.map(v => v.name) || [],
        organizationId: formData.organizationId || undefined,
        isActive: formData.isActive,
        isDefault: formData.isDefault
      };

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, templateData);
        toast.success('Template sikeresen frissítve!');
      } else {
        await createTemplate(templateData);
        toast.success('Template sikeresen létrehozva!');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      toast.error('Hiba történt a template mentése során!');
    }
  };

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (window.confirm(`Biztosan törölni szeretné a "${template.name}" template-et?`)) {
      try {
        await deleteTemplate(template.id);
        toast.success('Template sikeresen törölve!');
      } catch (error) {
        toast.error('Hiba történt a template törlése során!');
      }
    }
  };

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      await toggleTemplateActive(template.id, !template.isActive);
      toast.success(`Template ${template.isActive ? 'deaktiválva' : 'aktiválva'}!`);
    } catch (error) {
      toast.error('Hiba történt a template státusz módosítása során!');
    }
  };

  const handleSetDefault = async (template: EmailTemplate) => {
    try {
      await setDefaultTemplate(template.id, template.type);
      toast.success('Alapértelmezett template beállítva!');
    } catch (error) {
      toast.error('Hiba történt az alapértelmezett template beállítása során!');
    }
  };

  const handlePreviewTemplate = (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleInitializeDefaults = async () => {
    try {
      for (const template of defaultEmailTemplates) {
        await createTemplate({
          ...template,
          variables: defaultEmailVariables[template.type!]?.map(v => v.name) || []
        } as any);
      }
      toast.success('Alapértelmezett template-ek létrehozva!');
    } catch (error) {
      toast.error('Hiba történt az alapértelmezett template-ek létrehozása során!');
    }
  };

  const handleSendTestEmail = async () => {
    if (!user?.email) {
      toast.error('Nincs email cím megadva!');
      return;
    }

    try {
      await emailService.sendTestEmail(user.email);
      toast.success('Teszt email elküldve!');
    } catch (error) {
      toast.error('Hiba történt a teszt email küldése során!');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || template.type === typeFilter;
    const matchesOrg = organizationFilter === 'all' || template.organizationId === organizationFilter;
    
    return matchesSearch && matchesType && matchesOrg;
  });

  const templateStats = {
    total: templates.length,
    active: templates.filter(t => t.isActive).length,
    defaults: templates.filter(t => t.isDefault).length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email Template-ek</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Email sablonok kezelése és szerkesztése</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleSendTestEmail}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <EnvelopeIcon className="h-4 w-4" />
              <span>Teszt email</span>
            </button>
            <button
              onClick={handleCreateTemplate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Új template</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{templateStats.total}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Összes template</div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{templateStats.active}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Aktív</div>
          </div>
          <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{templateStats.defaults}</div>

            <div className="text-sm text-gray-600 dark:text-gray-400">Alapértelmezett</div>

          </div>

        </div>

      </div>



      {/* Filters */}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">

        <div className="flex flex-wrap gap-4 items-center">

          <div className="relative flex-1 min-w-64">

            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />

            <input

              type="text"

              placeholder="Keresés template-ekben..."

              value={searchTerm}

              onChange={(e) => setSearchTerm(e.target.value)}

              className="pl-10 pr-4 py-3 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500 text-sm"

            />

          </div>

          

          <div className="flex items-center space-x-2">

            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Típus:</span>

            <div className="flex space-x-1">

              <button

                onClick={() => setTypeFilter('all')}

                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${

                  typeFilter === 'all'

                    ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'

                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'

                }`}

              >

                Összes

              </button>

              {templateTypes.map((type) => (

                <button

                  key={type.value}

                  onClick={() => setTypeFilter(type.value)}

                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${

                    typeFilter === type.value

                      ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'

                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'

                  }`}

                >

                  {type.label}

                </button>

              ))}

            </div>

          </div>

        </div>

      </div>



      {/* Templates Grid */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

        {filteredTemplates.map((template) => {

          const typeConfig = getTypeConfig(template.type);

          

          return (

            <div key={template.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">

              {/* Template Header */}

              <div className="flex items-start justify-between mb-3">

                <div className="flex-1">

                  <div className="flex items-center space-x-2 mb-1">

                    <span className="text-lg">{typeConfig.label.split(' ')[0]}</span>

                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{template.name}</h3>

                    {template.isDefault && (

                      <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />

                    )}

                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{template.subject}</p>

                </div>

                <div className={`px-2 py-1 rounded-full text-xs font-medium ${

                  template.isActive 

                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 

                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'

                }`}>

                  {template.isActive ? 'Aktív' : 'Inaktív'}

                </div>

              </div>



              {/* Template Info */}

              <div className="space-y-2 mb-4">

                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">

                  <DocumentTextIcon className="h-4 w-4" />

                  <span>{typeConfig.label.split(' ').slice(1).join(' ')}</span>

                </div>

                {template.organizationId && (

                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">

                    <BuildingOfficeIcon className="h-4 w-4" />

                    <span>{organizations.find(org => org.id === template.organizationId)?.name || 'Ismeretlen szervezet'}</span>

                  </div>

                )}

                {template.description && (

                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{template.description}</p>

                )}

              </div>



              {/* Actions */}

              <div className="flex items-center justify-between">

                <div className="flex space-x-1">

                  <button

                    onClick={() => handlePreviewTemplate(template)}

                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"

                    title="Előnézet"

                  >

                    <EyeIcon className="h-4 w-4" />

                  </button>

                  <button

                    onClick={() => handleEditTemplate(template)}

                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"

                    title="Szerkesztés"

                  >

                    <PencilIcon className="h-4 w-4" />

                  </button>

                  <button

                    onClick={() => handleToggleActive(template)}

                    className={`p-2 rounded-lg transition-colors ${

                      template.isActive 

                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20' 

                        : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'

                    }`}

                    title={template.isActive ? 'Deaktiválás' : 'Aktiválás'}

                  >

                    {template.isActive ? <XMarkIcon className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}

                  </button>

                </div>

                

                <div className="flex space-x-1">

                  {!template.isDefault && (

                    <button

                      onClick={() => handleSetDefault(template)}

                      className="p-2 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"

                      title="Alapértelmezetté tétel"

                    >

                      <StarIcon className="h-4 w-4" />

                    </button>

                  )}

                  <button

                    onClick={() => handleDeleteTemplate(template)}

                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"

                    title="Törlés"

                  >

                    <TrashIcon className="h-4 w-4" />

                  </button>

                </div>

              </div>

            </div>

          );

        })}

      </div>



      {/* Empty State */}

      {filteredTemplates.length === 0 && (

        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">

          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />

          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Nincs template</h3>

          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Kezdje egy új template létrehozásával.</p>

          <div className="mt-6 flex justify-center space-x-3">

            <button

              onClick={handleCreateTemplate}

              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"

            >

              Új template létrehozása

            </button>

            {templates.length === 0 && (

              <button

                onClick={handleInitializeDefaults}

                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"

              >

                Alapértelmezett template-ek

              </button>

            )}

          </div>

        </div>

      )}



      {/* Create/Edit Modal */}

      {(showCreateModal || showEditModal) && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">

          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

            {/* Modal Header */}

            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 rounded-t-2xl">

              <div className="flex items-center justify-between">

                <div className="flex items-center space-x-2">

                  <DocumentTextIcon className="h-5 w-5 text-white" />

                  <div>

                    <h3 className="text-lg font-semibold text-white">

                      {editingTemplate ? 'Template szerkesztése' : 'Új template'}

                    </h3>

                  </div>

                </div>

                <button

                  onClick={() => {

                    setShowCreateModal(false);

                    setShowEditModal(false);

                    setEditingTemplate(null);

                    resetForm();

                  }}

                  className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"

                >

                  <XMarkIcon className="h-5 w-5" />

                </button>

              </div>

            </div>



            {/* Modal Body */}

            <div className="p-5 space-y-4">

              {/* Basic Information */}

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">

                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">

                  <Cog6ToothIcon className="h-4 w-4 mr-2" />

                  Alapadatok

                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                  <div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">

                      Név <span className="text-red-500">*</span>

                    </label>

                    <input

                      type="text"

                      value={formData.name}

                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}

                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors text-sm"

                      placeholder="Template neve"

                    />

                  </div>



                  <div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">

                      Típus <span className="text-red-500">*</span>

                    </label>

                    <div className="relative">

                      <select

                        value={formData.type}

                        onChange={(e) => setFormData({ ...formData, type: e.target.value as EmailTemplate['type'] })}

                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors appearance-none text-sm"

                      >

                        {templateTypes.map(type => (

                          <option key={type.value} value={type.value}>

                            {type.label}

                          </option>

                        ))}

                      </select>

                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">

                        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />

                        </svg>

                      </div>

                    </div>

                  </div>



                  <div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">

                      Szervezet

                    </label>

                    <div className="relative">

                      <select

                        value={formData.organizationId}

                        onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}

                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors appearance-none text-sm"

                      >

                        <option value="">🌍 Globális</option>

                        {organizations.map(org => (

                          <option key={org.id} value={org.id}>

                            🏢 {org.name}

                          </option>

                        ))}

                      </select>

                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">

                        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />

                        </svg>

                      </div>

                    </div>

                  </div>

                </div>



                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">

                  <div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">

                      Tárgy <span className="text-red-500">*</span>

                    </label>

                    <input

                      type="text"

                      value={formData.subject}

                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}

                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors text-sm"

                      placeholder="Email tárgya"

                    />

                  </div>



                  <div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">

                      Leírás

                    </label>

                    <input

                      type="text"

                      value={formData.description}

                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}

                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors text-sm"

                      placeholder="Template leírása (opcionális)"

                    />

                  </div>

                </div>



                <div className="flex items-center space-x-4 mt-3">

                  <label className="flex items-center cursor-pointer">

                    <div className="relative">

                      <input

                        type="checkbox"

                        checked={formData.isActive}

                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}

                        className="sr-only"

                      />

                      <div className={`w-4 h-4 rounded border-2 transition-colors ${

                        formData.isActive 

                          ? 'bg-green-500 border-green-500' 

                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'

                      }`}>

                        {formData.isActive && (

                          <CheckIcon className="h-3 w-3 text-white absolute top-0 left-0" />

                        )}

                      </div>

                    </div>

                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">✅ Aktív</span>

                  </label>



                  <label className="flex items-center cursor-pointer">

                    <div className="relative">

                      <input

                        type="checkbox"

                        checked={formData.isDefault}

                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}

                        className="sr-only"

                      />

                      <div className={`w-4 h-4 rounded border-2 transition-colors ${

                        formData.isDefault 

                          ? 'bg-yellow-500 border-yellow-500' 

                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'

                      }`}>

                        {formData.isDefault && (

                          <StarIcon className="h-3 w-3 text-white absolute top-0 left-0 fill-current" />

                        )}

                      </div>

                    </div>

                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">⭐ Alapértelmezett</span>

                  </label>

                </div>

              </div>



              {/* Content */}

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">

                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">

                  <DocumentTextIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />

                  HTML tartalom <span className="text-red-500">*</span>

                </h4>

                <textarea

                  value={formData.htmlContent}

                  onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}

                  rows={8}

                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors font-mono text-sm resize-none"

                  placeholder="HTML email tartalom"

                />

              </div>



              {/* Available Variables */}

              {(defaultEmailVariables[formData.type] || []).length > 0 && (

                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">

                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">

                    <Cog6ToothIcon className="h-4 w-4 mr-2 text-purple-600 dark:text-purple-400" />

                    Elérhető változók

                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">

                    {(defaultEmailVariables[formData.type] || []).map(variable => (

                      <div key={variable.name} className="flex items-center space-x-2 p-2 bg-white dark:bg-gray-700 rounded border border-purple-200 dark:border-purple-700">

                        <code className="bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-mono">

                          {`{{${variable.name}}}`}

                        </code>

                        <span className="text-xs text-gray-600 dark:text-gray-400 flex-1">{variable.description}</span>

                      </div>

                    ))}

                  </div>

                </div>

              )}

            </div>



            {/* Modal Footer */}

            <div className="bg-gray-50 dark:bg-gray-800 px-5 py-3 rounded-b-2xl border-t border-gray-200 dark:border-gray-700">

              <div className="flex items-center justify-between">

                <div className="text-xs text-gray-500 dark:text-gray-400">

                  <span className="text-red-500">*</span> Kötelező mezők

                </div>

                <div className="flex space-x-3">

                  <button

                    onClick={() => {

                      setShowCreateModal(false);

                      setShowEditModal(false);

                      setEditingTemplate(null);

                      resetForm();

                    }}

                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm"

                  >

                    Mégse

                  </button>

                  <button

                    onClick={handleSaveTemplate}

                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg text-sm"

                  >

                    {editingTemplate ? '💾 Frissítés' : '✨ Létrehozás'}

                  </button>

                </div>

              </div>

            </div>

          </div>

        </div>

      )}



      {/* Preview Modal */}

      {showPreview && previewTemplate && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">

          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">

            <div className="p-6">

              <div className="flex items-center justify-between mb-4">

                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Template előnézet</h3>

                <button

                  onClick={() => setShowPreview(false)}

                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"

                >

                  <XMarkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />

                </button>

              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">

                <div className="mb-4">

                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{previewTemplate.subject}</h4>

                </div>

                <div 

                  className="prose max-w-none"

                  dangerouslySetInnerHTML={{ __html: previewTemplate.htmlContent }}

                />

              </div>

            </div>

          </div>

        </div>

      )}

    </div>

  );

};



export default EmailTemplateManager; 